import React, {useCallback, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {useQuery, useQueryClient} from 'react-query';
import {UserAPIController} from '../../../../../../../../api/user-api/user-api-controller';
import {UserAPIRequestImpl} from '../../../../../../../../api/user-api/user-api-request';
import {useApiCalls} from '../../../../../../../../hooks/init-calls';
import {useStoreContext} from '../../../../../../../../store/store-context';
import {Avatar} from '../../../../../../../../ui/atoms/avatar';
import {MagnifyingGlassIcon} from '../../../../../../../../ui/atoms/icons/magnifying-glass';
import {Tooltip} from '../../../../../../../../ui/molecules/tooltip';
import {activateNotification} from '../../../../../../../../ui/molecules/notification/activate-notification';
import {ItemLoaderLine} from '../../../../../../../../ui/organisms/item-loader-line';
import {setFirebaseData} from '../../../../../../../../utils/firebase-handler';
import {generateRandomDigits} from '../../../../../../../../utils/generate-random-numbers';
import {
  ConfirmCopy,
  DangerButton,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  GroupMore,
  GroupPill,
  MemberCard,
  MemberEmpty,
  MemberGroupsCell,
  MemberIdentity,
  MemberIdentityText,
  MemberMeta,
  MemberName,
  MemberNameLine,
  MemberPageButton,
  MemberPagination,
  MemberRoleBadge,
  MemberSearchBar,
  MemberSearchIcon,
  MemberSearchInput,
  ReactivateMemberButton,
  RemoveSuspendedMemberButton,
  SecondaryButton,
  SuspendedActions,
  SuspendedDateCell,
  SuspendedTableHeader,
  SuspendedTableRow,
} from '../../member-redesign.styles';
import {getManagerIds, isWorkspaceManager} from '../../member-role';
import {removeMemberFromPaginatedCache} from '../member-list-cache';

const memberName = (member: any) =>
  member?.firstName && member?.lastName
    ? `${member.firstName} ${member.lastName}`
    : member?.email || 'Unnamed member';

const suspendedDate = (member: any) => {
  const value =
    member?.deactivatedAt ||
    member?.suspendedAt ||
    member?.deactivationDate ||
    member?.updatedAt;
  const date = dayjs(value);

  return value && date.isValid() ? date.format('MMM D, YYYY') : '—';
};

const uniqueGroups = (groups: any[]) => {
  const result = new Map<string, any>();
  groups.filter(Boolean).forEach((group: any) => {
    const key = group.id || group.name;
    if (key) result.set(key, group);
  });
  return Array.from(result.values());
};

export const Deactivate = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [removingMember, setRemovingMember] = useState<any>(null);
  const [actionId, setActionId] = useState('');
  const queryClient = useQueryClient();
  const {
    groupStore: {groups},
    usersStore: {users: workspaceUsers},
  } = useStoreContext();
  const {initGroupMembers} = useApiCalls();

  const deactivatedUsersQuery = useQuery(
    ['deactivatedUsers', page],
    async () => {
      const controller = new UserAPIController(new UserAPIRequestImpl());
      return controller.fetchPaginatedDeactivatedUsers(page);
    },
  );
  const groupMembersQuery = useQuery(['initGroupMembers'], initGroupMembers);
  const response = deactivatedUsersQuery.data || {};
  const pageMembers = useMemo(
    () => (Array.isArray(response?.results) ? response.results : []),
    [response?.results],
  );

  const enrichedMembers = useMemo(() => {
    const membership = groupMembersQuery.data;

    return pageMembers.map((member: any) => {
      const membershipGroupIds = (membership?.get?.(member.id) || []).map(
        (entry: any) => entry.group,
      );
      const membershipGroups = groups.filter((group: any) =>
        membershipGroupIds.includes(group.id),
      );
      const directGroups = [
        ...(Array.isArray(member.group) ? member.group : []),
        ...(Array.isArray(member.groups) ? member.groups : []),
      ]
        .map((group: any) =>
          typeof group === 'string'
            ? groups.find((candidate: any) => candidate.id === group)
            : group,
        )
        .filter(Boolean);

      return {
        ...member,
        group: uniqueGroups([...membershipGroups, ...directGroups]),
      };
    });
  }, [groupMembersQuery.data, groups, pageMembers]);

  const roleSource = useMemo(() => {
    const members = new Map<string, any>();
    [...(workspaceUsers || []), ...enrichedMembers].forEach((member: any) =>
      members.set(member.id, member),
    );
    return Array.from(members.values());
  }, [enrichedMembers, workspaceUsers]);
  const managerIds = useMemo(() => getManagerIds(roleSource), [roleSource]);
  const visibleMembers = enrichedMembers.filter((member: any) =>
    [
      memberName(member),
      member.email,
      member.jobTitle,
      ...(member.group || []).map((group: any) => group.name),
    ]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(search.trim().toLowerCase()),
      ),
  );
  const totalPages = Number(response?.totalPages || 1);
  const queryKey = useMemo(() => ['deactivatedUsers', page], [page]);

  const completeAction = useCallback(
    async (memberId: string) => {
      await queryClient.cancelQueries(queryKey);
      removeMemberFromPaginatedCache(queryClient, queryKey, memberId);
      queryClient.invalidateQueries(['deactivatedUsers']);
      queryClient.invalidateQueries(['activeUsers']);
      queryClient.invalidateQueries(['workspace-member-counts']);
      setFirebaseData('updates/user', generateRandomDigits(16));
    },
    [queryClient, queryKey],
  );

  const reactivateMember = async (member: any) => {
    setActionId(`reactivate-${member.id}`);
    const controller = new UserAPIController(new UserAPIRequestImpl());
    const success = Boolean(await controller.activateUser(member.id));

    if (success) {
      activateNotification({
        title: 'Member reactivated',
        content: `${memberName(member)} can access the workspace again.`,
        kind: 'success',
      });
      await completeAction(member.id);
    }
    setActionId('');
  };

  const removeMember = async () => {
    if (!removingMember) return;
    setActionId(`remove-${removingMember.id}`);
    const controller = new UserAPIController(new UserAPIRequestImpl());
    const success = Boolean(await controller.deleteUser(removingMember.id));

    if (success) {
      activateNotification({
        title: 'Member removed',
        content: `${memberName(
          removingMember,
        )} was removed from the workspace.`,
        kind: 'success',
      });
      await completeAction(removingMember.id);
      setRemovingMember(null);
    }
    setActionId('');
  };

  return (
    <>
      <MemberCard>
        <MemberSearchBar>
          <MemberSearchInput
            aria-label="Search suspended members"
            placeholder="Search members"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <MemberSearchIcon aria-hidden="true">
            <MagnifyingGlassIcon />
          </MemberSearchIcon>
        </MemberSearchBar>

        <SuspendedTableHeader>
          <div>Name</div>
          <div>Group(s)</div>
          <div>Suspended</div>
          <div aria-label="Actions" />
        </SuspendedTableHeader>

        {deactivatedUsersQuery.isLoading || groupMembersQuery.isLoading ? (
          <div style={{padding: 24}}>
            <ItemLoaderLine />
          </div>
        ) : visibleMembers.length === 0 ? (
          <MemberEmpty>
            {search
              ? 'No suspended members match your search.'
              : 'There are no suspended members.'}
          </MemberEmpty>
        ) : (
          visibleMembers.map((member: any) => (
            <SuspendedTableRow key={member.id}>
              <MemberIdentity>
                <Avatar
                  name={memberName(member)}
                  userId={member.id}
                  src={member.avatar?.url || ''}
                  size="md"
                  tooltip={true}
                  timezone={member.timezone}
                  customUsers={roleSource}
                />
                <MemberIdentityText>
                  <MemberNameLine>
                    <MemberName>{memberName(member)}</MemberName>
                    {isWorkspaceManager(member, managerIds) && (
                      <MemberRoleBadge>Manager</MemberRoleBadge>
                    )}
                  </MemberNameLine>
                  <MemberMeta>{member.jobTitle || '—'}</MemberMeta>
                </MemberIdentityText>
              </MemberIdentity>

              <MemberGroupsCell>
                {member.group?.length ? (
                  <>
                    <GroupPill title={member.group[0].name}>
                      {member.group[0].name}
                    </GroupPill>
                    {member.group.length > 1 && (
                      <Tooltip
                        BodyTextNodeType={GroupMore}
                        text={`+${member.group.length - 1}`}
                        noCapitalize
                        bodyStyle={{cursor: 'help'}}
                        tooltipBody={member.group
                          .slice(1)
                          .map((group: any) => group.name)
                          .join(', ')}
                      />
                    )}
                  </>
                ) : (
                  '—'
                )}
              </MemberGroupsCell>

              <SuspendedDateCell>{suspendedDate(member)}</SuspendedDateCell>

              <SuspendedActions>
                <ReactivateMemberButton
                  type="button"
                  disabled={Boolean(actionId)}
                  onClick={() => reactivateMember(member)}>
                  {actionId === `reactivate-${member.id}`
                    ? 'Reactivating…'
                    : 'Reactivate'}
                </ReactivateMemberButton>
                <RemoveSuspendedMemberButton
                  type="button"
                  disabled={Boolean(actionId)}
                  onClick={() => setRemovingMember(member)}>
                  Remove
                </RemoveSuspendedMemberButton>
              </SuspendedActions>
            </SuspendedTableRow>
          ))
        )}

        {totalPages > 1 && !search && (
          <MemberPagination>
            <MemberPageButton
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              ‹
            </MemberPageButton>
            {Array.from({length: totalPages}, (_, index) => index + 1)
              .filter(
                (number) =>
                  number === 1 ||
                  number === totalPages ||
                  Math.abs(number - page) <= 1,
              )
              .map((number) => (
                <MemberPageButton
                  type="button"
                  key={number}
                  $active={number === page}
                  onClick={() => setPage(number)}>
                  {number}
                </MemberPageButton>
              ))}
            <MemberPageButton
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}>
              ›
            </MemberPageButton>
          </MemberPagination>
        )}
      </MemberCard>

      {removingMember && (
        <DialogOverlay>
          <Dialog role="dialog" aria-modal="true" aria-label="Remove member">
            <DialogHeader>
              <DialogTitle>Remove member</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ConfirmCopy>
                Remove {memberName(removingMember)} and their workspace access?
                This action cannot be undone.
              </ConfirmCopy>
            </DialogBody>
            <DialogFooter>
              <SecondaryButton
                type="button"
                disabled={Boolean(actionId)}
                onClick={() => setRemovingMember(null)}>
                Cancel
              </SecondaryButton>
              <DangerButton
                type="button"
                disabled={Boolean(actionId)}
                onClick={removeMember}>
                {actionId === `remove-${removingMember.id}`
                  ? 'Removing…'
                  : 'Remove member'}
              </DangerButton>
            </DialogFooter>
          </Dialog>
        </DialogOverlay>
      )}
    </>
  );
};
